import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';

const DEFAULT_SEARCH_PROVIDERS = Object.freeze({
  yahoo: true,
  msci: false,
  sp: false,
  iceBofa: false,
  famaFrench: false,
  ftseRussell: false,
});

const SEARCH_PROVIDER_FLAGS = [
  {
    key: 'yahoo',
    queryParam: null,
    label: 'Yahoo Finance',
    accent: '#1d4ed8',
    background: 'rgba(29, 78, 216, 0.12)',
  },
  {
    key: 'msci',
    queryParam: 'include_msci',
    label: 'MSCI',
    accent: '#0f766e',
    background: 'rgba(15, 118, 110, 0.12)',
  },
  {
    key: 'sp',
    queryParam: 'include_sp',
    label: 'S&P',
    accent: '#c2410c',
    background: 'rgba(194, 65, 12, 0.12)',
  },
  {
    key: 'iceBofa',
    queryParam: 'include_ice_bofa',
    label: 'ICE BofA',
    accent: '#be123c',
    background: 'rgba(190, 18, 60, 0.12)',
  },
  {
    key: 'famaFrench',
    queryParam: 'include_fama_french',
    label: 'Fama/French',
    accent: '#4d7c0f',
    background: 'rgba(77, 124, 15, 0.12)',
  },
  {
    key: 'ftseRussell',
    queryParam: 'include_ftse_russell',
    label: 'FTSE / Russell',
    accent: '#334155',
    background: 'rgba(51, 65, 85, 0.12)',
  },
];

const RESULT_PROVIDER_ORDER = ['MSCI', 'S&P', 'FTSE', 'RUSSELL', 'ICE_BOFA', 'FAMA_FRENCH', 'YAHOO'];

const RESULT_PROVIDER_META = {
  YAHOO: {
    label: 'Yahoo Finance',
    accent: '#1d4ed8',
    background: 'rgba(29, 78, 216, 0.08)',
    border: 'rgba(29, 78, 216, 0.24)',
  },
  MSCI: {
    label: 'MSCI',
    accent: '#0f766e',
    background: 'rgba(15, 118, 110, 0.08)',
    border: 'rgba(15, 118, 110, 0.24)',
  },
  'S&P': {
    label: 'S&P',
    accent: '#c2410c',
    background: 'rgba(194, 65, 12, 0.08)',
    border: 'rgba(194, 65, 12, 0.24)',
  },
  ICE_BOFA: {
    label: 'ICE BofA',
    accent: '#be123c',
    background: 'rgba(190, 18, 60, 0.08)',
    border: 'rgba(190, 18, 60, 0.24)',
  },
  FAMA_FRENCH: {
    label: 'Fama/French',
    accent: '#4d7c0f',
    background: 'rgba(77, 124, 15, 0.08)',
    border: 'rgba(77, 124, 15, 0.24)',
  },
  FTSE: {
    label: 'FTSE',
    accent: '#334155',
    background: 'rgba(51, 65, 85, 0.08)',
    border: 'rgba(51, 65, 85, 0.24)',
  },
  RUSSELL: {
    label: 'Russell',
    accent: '#b45309',
    background: 'rgba(180, 83, 9, 0.08)',
    border: 'rgba(180, 83, 9, 0.24)',
  },
};

const buildSearchProviders = (searchProviders, includeMsci) => ({
  ...DEFAULT_SEARCH_PROVIDERS,
  ...(searchProviders || {}),
  msci: searchProviders?.msci ?? includeMsci,
});

const getProviderMeta = (provider) => RESULT_PROVIDER_META[provider] || RESULT_PROVIDER_META.YAHOO;

const groupResultsByProvider = (results = []) => {
  const grouped = new Map();

  results.forEach((result) => {
    const provider = result?.provider || 'YAHOO';
    if (!grouped.has(provider)) {
      grouped.set(provider, []);
    }
    grouped.get(provider).push(result);
  });

  const orderedGroups = RESULT_PROVIDER_ORDER
    .filter((provider) => grouped.has(provider))
    .map((provider) => ({ provider, items: grouped.get(provider) }));
  const unorderedGroups = Array.from(grouped.entries())
    .filter(([provider]) => !RESULT_PROVIDER_ORDER.includes(provider))
    .map(([provider, items]) => ({ provider, items }));

  return [...orderedGroups, ...unorderedGroups];
};

const SearchComponent = ({
  onSelect,
  baseUrl = '',
  initialSelectedEtf = null,
  showPreviewChart = true,
  includeMsci = false,
  searchProviders = null,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectionLoading, setSelectionLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedEtf, setSelectedEtf] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const normalizedBaseUrl = baseUrl.replace(/\/$/, '');
  const effectiveProviders = buildSearchProviders(searchProviders, includeMsci);
  const activeSearchProviders = SEARCH_PROVIDER_FLAGS.filter(({ key }) => effectiveProviders[key]);
  const searchCoverageLabel = activeSearchProviders.map((provider) => provider.label).join(', ');
  const supportsBenchmarkProviders = activeSearchProviders.some(({ key }) => key !== 'yahoo');

  useEffect(() => {
    if (initialSelectedEtf?.symbol) {
      setSelectedEtf(initialSelectedEtf);
      setSearchQuery(initialSelectedEtf.symbol);
      setSearchResults([]);
      setErrorMessage('');
      return;
    }

    if (initialSelectedEtf === null) {
      setSelectedEtf(null);
    }
  }, [initialSelectedEtf]);

  const parseApiJson = async (response, contextLabel) => {
    const rawText = await response.text();
    const trimmedText = rawText.trim();

    try {
      const parsed = trimmedText ? JSON.parse(trimmedText) : {};
      return parsed;
    } catch {
      const looksLikeHtml =
        trimmedText.toLowerCase().startsWith('<!doctype') ||
        trimmedText.toLowerCase().startsWith('<html');
      if (looksLikeHtml) {
        throw new Error(
          `${contextLabel} returned HTML instead of JSON. Check API base URL/proxy target and backend port.`,
        );
      }
      throw new Error(`${contextLabel} returned invalid JSON.`);
    }
  };

  const runSearch = async (query) => {
    const trimmedQuery = query.trim();
    if (!trimmedQuery) {
      setHasSearched(false);
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    setHasSearched(true);
    setErrorMessage('');

    try {
      const searchUrl = new URL(`${normalizedBaseUrl}/api/etf/search`, window.location.origin);
      searchUrl.searchParams.set('q', trimmedQuery);
      searchUrl.searchParams.set('limit', '25');

      activeSearchProviders.forEach((provider) => {
        if (provider.queryParam) {
          searchUrl.searchParams.set(provider.queryParam, '1');
        }
      });

      const response = await fetch(searchUrl.toString());
      const payload = await parseApiJson(response, 'Asset search API');

      if (!response.ok) {
        throw new Error(payload.error || 'Asset search failed');
      }

      setSearchResults(Array.isArray(payload.results) ? payload.results : []);
    } catch (error) {
      console.error('Asset search error:', error);
      setSearchResults([]);
      setErrorMessage(error.message || 'Unable to search symbols');
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSelectEtf = async (etfResult) => {
    setSelectionLoading(true);
    setErrorMessage('');

    try {
      const response = await fetch(`${normalizedBaseUrl}/api/etf/collect-returns`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          symbol: etfResult.symbol,
          name: etfResult.name,
          start_date: etfResult.start_date || null,
          provider: etfResult.provider || null,
          index_code: etfResult.index_code || null,
        }),
      });

      const payload = await parseApiJson(response, 'Asset collect API');
      if (!response.ok) {
        throw new Error(payload.error || 'Return collection failed');
      }

      const selected = {
        symbol: payload.symbol || etfResult.symbol,
        name: payload.name || etfResult.name || etfResult.symbol,
        start_date: payload.start_date || etfResult.start_date || null,
        end_date: payload.end_date || null,
        provider: payload.provider || etfResult.provider || null,
        index_code: payload.index_code || etfResult.index_code || null,
        cumulative_returns: Array.isArray(payload.cumulative_returns) ? payload.cumulative_returns : [],
      };

      setSelectedEtf(selected);
      setSearchQuery(selected.symbol);
      setSearchResults([]);

      if (typeof onSelect === 'function') {
        onSelect(selected.symbol, selected);
      }
    } catch (error) {
      console.error('Asset return collection error:', error);
      setErrorMessage(error.message || 'Unable to collect symbol returns');
    } finally {
      setSelectionLoading(false);
    }
  };

  const handleSearchSubmit = () => {
    const trimmedQuery = searchQuery.trim();
    if (!trimmedQuery || searchLoading || selectionLoading) {
      return;
    }
    runSearch(trimmedQuery);
  };

  const handleInputKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSearchSubmit();
    }
  };

  const renderStartDate = (startDate) => (startDate ? startDate : 'Unavailable');
  const selectedRowCount = Array.isArray(selectedEtf?.cumulative_returns)
    ? selectedEtf.cumulative_returns.length
    : 0;
  const formatPct = (value) => `${(Number(value) * 100).toFixed(1)}%`;
  const loadingLabel = selectionLoading
    ? 'Fetching monthly returns...'
    : `Searching ${searchCoverageLabel || 'selected sources'}...`;
  const groupedResults = groupResultsByProvider(searchResults);
  const selectedProviderMeta = getProviderMeta(selectedEtf?.provider || 'YAHOO');

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <TextField
          label={supportsBenchmarkProviders ? 'Search Symbols, ETFs, or Benchmarks' : 'Search Stock or ETF Symbol/Name'}
          value={searchQuery}
          onChange={(e) => {
            const nextQuery = e.target.value;
            setSearchQuery(nextQuery);
            if (!nextQuery.trim()) {
              setHasSearched(false);
              setSearchResults([]);
              setErrorMessage('');
            }
          }}
          onKeyDown={handleInputKeyDown}
          fullWidth
          InputLabelProps={{ shrink: true }}
          sx={{ height: '50px' }}
        />
        <Button
          variant="contained"
          onClick={handleSearchSubmit}
          disabled={!searchQuery.trim() || searchLoading || selectionLoading}
          sx={{
            whiteSpace: 'nowrap',
            minWidth: 104,
            background: 'linear-gradient(135deg, #0f172a 0%, #1d4ed8 100%)',
            boxShadow: '0 10px 24px rgba(29, 78, 216, 0.2)',
          }}
        >
          Search
        </Button>
      </Box>

      <Paper
        variant="outlined"
        sx={{
          mt: 1.5,
          p: 1.5,
          borderRadius: 2,
          background: 'linear-gradient(180deg, rgba(248, 250, 252, 0.92) 0%, rgba(241, 245, 249, 0.92) 100%)',
        }}
      >
        <Typography variant="caption" sx={{ display: 'block', mb: 1, fontWeight: 700, color: 'text.secondary' }}>
          Searching Across
        </Typography>
        <Stack direction="row" useFlexGap flexWrap="wrap" gap={1}>
          {activeSearchProviders.map((provider) => (
            <Chip
              key={provider.key}
              label={provider.label}
              size="small"
              sx={{
                fontWeight: 700,
                color: provider.accent,
                backgroundColor: provider.background,
                border: `1px solid ${provider.accent}33`,
              }}
            />
          ))}
        </Stack>
      </Paper>

      {(searchLoading || selectionLoading) && (
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, gap: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="body2">{loadingLabel}</Typography>
        </Box>
      )}

      {errorMessage && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {errorMessage}
        </Alert>
      )}

      {!searchLoading && !selectionLoading && hasSearched && !errorMessage && searchResults.length === 0 && (
        <Alert severity="info" sx={{ mt: 1 }}>
          {`No matches found across ${searchCoverageLabel || 'the active providers'}.`}
        </Alert>
      )}

      {groupedResults.length > 0 && (
        <Paper
          variant="outlined"
          sx={{
            mt: 2,
            maxHeight: 340,
            overflowY: 'auto',
            borderRadius: 2,
            backgroundColor: 'rgba(255, 255, 255, 0.92)',
          }}
        >
          {groupedResults.map(({ provider, items }, index) => {
            const providerMeta = getProviderMeta(provider);
            return (
              <Box key={provider}>
                {index > 0 && <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }} />}
                <Box
                  sx={{
                    px: 1.5,
                    py: 1.25,
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                    backgroundColor: providerMeta.background,
                    borderBottom: '1px solid',
                    borderBottomColor: providerMeta.border,
                  }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                    <Chip
                      label={providerMeta.label}
                      size="small"
                      sx={{
                        fontWeight: 700,
                        color: providerMeta.accent,
                        backgroundColor: '#fff',
                        border: `1px solid ${providerMeta.border}`,
                      }}
                    />
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                      {`${items.length} result${items.length === 1 ? '' : 's'}`}
                    </Typography>
                  </Stack>
                </Box>
                <List disablePadding>
                  {items.map((result) => (
                    <ListItem key={`${result.provider || 'UNKNOWN'}:${result.symbol}`} disablePadding>
                      <ListItemButton
                        onClick={() => handleSelectEtf(result)}
                        disabled={selectionLoading}
                        sx={{
                          alignItems: 'flex-start',
                          py: 1.5,
                          px: 1.5,
                          transition: 'background-color 0.18s ease, transform 0.18s ease',
                          '&:hover': {
                            backgroundColor: providerMeta.background,
                            transform: 'translateX(2px)',
                          },
                        }}
                      >
                        <Box sx={{ width: '100%' }}>
                          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1.5}>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                {result.symbol}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {result.name}
                              </Typography>
                            </Box>
                            {result.asset_type && (
                              <Chip
                                label={result.asset_type}
                                size="small"
                                variant="outlined"
                                sx={{ borderColor: providerMeta.border, color: providerMeta.accent, fontWeight: 600 }}
                              />
                            )}
                          </Stack>
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: 'text.secondary' }}>
                            {result.start_date ? `History starts ${result.start_date}` : 'Monthly return series available.'}
                          </Typography>
                        </Box>
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Box>
            );
          })}
        </Paper>
      )}

      {selectedEtf && (
        <Box sx={{ mt: 1.5 }}>
          <Stack direction="row" alignItems="center" useFlexGap flexWrap="wrap" gap={1}>
            <Chip
              label={selectedProviderMeta.label}
              size="small"
              sx={{
                fontWeight: 700,
                color: selectedProviderMeta.accent,
                backgroundColor: selectedProviderMeta.background,
                border: `1px solid ${selectedProviderMeta.border}`,
              }}
            />
            <Typography variant="caption" sx={{ display: 'block' }}>
              {`Selected Source: ${selectedEtf.symbol} - ${selectedEtf.name} | Start: ${renderStartDate(selectedEtf.start_date)}${
                selectedEtf.end_date ? ` | End: ${selectedEtf.end_date}` : ''
              }`}
            </Typography>
          </Stack>

          {showPreviewChart && selectedRowCount > 1 && (
            <Box sx={{ mt: 1, height: 220, border: '1px solid #ddd', borderRadius: 1, p: 1 }}>
              <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                Cumulative Return (USD)
              </Typography>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={selectedEtf.cumulative_returns}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" minTickGap={40} />
                  <YAxis tickFormatter={formatPct} width={56} />
                  <RechartsTooltip
                    formatter={(value) => formatPct(value)}
                    labelFormatter={(label) => `Date: ${label}`}
                  />
                  <Line
                    type="monotone"
                    dataKey="cumulative_return"
                    stroke="#1976d2"
                    dot={false}
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default SearchComponent;
